import { createFileRoute, Navigate, Outlet, redirect, useLocation } from '@tanstack/react-router';

import { AppLayout } from '@/layout/AppLayout';
import { SlugPendingSkeleton } from '@/layout/AppLayoutSkeleton';
import { AuthenticatedNotFound } from '@/components/not-found';
import { adoptSignedInSession, switchTenantRequest } from '@/api/mutations/useAuth';
import { membershipsQueryOptions } from '@/api/queries/useMemberships';
import { orgSettingsQueryOptions } from '@/api/queries/useOrgSettings';
import { profileQueryOptions, useProfileQuery } from '@/api/queries/useProfile';
import { redirectBrowser, reloadBrowser } from '@/lib/browser-redirect';
import { resolveSlugMismatch } from '@/lib/auth/resolveSlugMismatch';
import { useAuthStore, withWorkspaceSwitchSplash } from '@/stores/auth-store';
import { useLanguageInitialization } from '@/hooks/useLanguageInitialization';
import { resolveLockRedirect } from '@/lib/auth/resolveLockRedirect';
import { requireAuthenticated } from '@/lib/auth/authSnapshot';
import { LOCK_ROUTE_SEGMENTS, lockRouteSegment } from '@/constants/routes';

export const Route = createFileRoute('/$slug')({
  component: SlugLayout,
  notFoundComponent: AuthenticatedNotFound,
  // $slug is the pending boundary whenever it is not already committed: a
  // cold load, a sign-in into the bare slug, and also leaving an in-app 404
  // (router-core never retains a _notFound match, so there the skeleton
  // replaces the chrome for the next leaf load). It stays the boundary until
  // the whole tree commits: any delay is a blank gap after the boot skeleton,
  // and a minimum would only hold back the page.
  pendingComponent: SlugPendingSkeleton,
  pendingMs: 0,
  pendingMinMs: 0,
  beforeLoad: async ({ params, context, location, preload }) => {
    const auth = requireAuthenticated();

    // Validate that the URL slug matches the authenticated user's org slug
    const urlSlug = params.slug;
    const userSlug = auth.slug;

    // Superadmins should never access tenant-specific routes
    if (auth.isSuperAdmin) {
      throw redirect({
        to: '/admin/tenants'
      });
    }

    // Partners have their own area; keep them out of the customer app
    if (auth.isPartner) {
      throw redirect({
        to: '/partner'
      });
    }

    if (urlSlug !== userSlug) {
      // Re-scoping the session is a side effect no preload may trigger:
      // `defaultPreload` is unset today, but a future `'intent'` must not let
      // a link hover silently switch the user's workspace.
      if (!preload) {
        const resolution = await resolveSlugMismatch(
          {
            urlSlug,
            sessionTenantId: useAuthStore.getState().tenant?._id ?? null,
            href: location.href
          },
          {
            fetchMemberships: () => context.queryClient.ensureQueryData(membershipsQueryOptions),
            // The guard's switch is speculative — a failure falls back to the
            // plain redirect below, so the global 403 toast would be noise on
            // top of an already-handled outcome.
            switchTenant: (tenantId) =>
              withWorkspaceSwitchSplash(() =>
                switchTenantRequest(tenantId, { suppressForbiddenToast: true })
              ),
            adoptSession: adoptSignedInSession,
            loadFullPage: (href) => {
              // location.assign to the CURRENT document URL performs a
              // same-document navigation when a #fragment is involved — no
              // reload, and the old tenant's caches would live on under the
              // new session. Only reload() guarantees the full load.
              const target = new URL(href, window.location.origin).href;
              if (target === window.location.href) {
                reloadBrowser();
              } else {
                redirectBrowser(href);
              }
            }
          }
        );
        if (resolution.action === 'switched') {
          // The browser is unloading into a full document load of the same
          // href (see resolveSlugMismatch for why an SPA continuation is
          // unsafe) — park this guard so the route tree never resolves
          // against the old tenant's caches.
          return new Promise<never>(() => {});
        }
      }

      // No usable membership for the URL's workspace (or a preload): bounce
      // to the session tenant's dashboard, today's behavior.
      throw redirect({
        to: '/$slug',
        params: { slug: userSlug }
      });
    }

    // Mandatory setup gate (ADR 0011): until the Ersteinrichtung is
    // completed, org-managers land in the wizard and everyone else on the
    // waiting screen — the org is unusable before that. Once it is complete,
    // a seat overrun locks the org on its own screen instead.
    // Org settings are preloaded alongside the profile so the language
    // initialization's org-default fallback has data on first render.
    // Best-effort: the data is only a language fallback, so a failed fetch
    // must not take down the whole route tree — the profile fetch alone is
    // load-bearing (setup gate).
    const [profile] = await Promise.all([
      context.queryClient.ensureQueryData(profileQueryOptions),
      context.queryClient.ensureQueryData(orgSettingsQueryOptions).catch(() => undefined)
    ]);
    // Keyed on the lock the profile RESOLVES to, not on lock-area membership:
    // with two independent lock kinds, sitting on one lock screen is no reason
    // to skip the redirect to the other.
    const lockRedirect = resolveLockRedirect(profile);
    if (lockRedirect && !location.pathname.includes(lockRouteSegment(lockRedirect))) {
      throw redirect({ to: lockRedirect, params: { slug: urlSlug } });
    }

    return {
      slug: urlSlug
    };
  }
});

function SlugLayout() {
  const { slug } = Route.useParams();
  const { pathname } = useLocation();
  // Runs here (not in AppLayout) so setup screens also honor the user's /
  // organization's language preference.
  useLanguageInitialization();
  const { data: profile } = useProfileQuery();
  // The Ersteinrichtung (and its waiting screen) is the whole product until
  // completedAt — it renders full-screen without the app chrome; the nav
  // would only offer places the setup gate bounces back from. Membership is
  // the right key HERE (chrome or no chrome), unlike the redirect below.
  const isLockScreen = LOCK_ROUTE_SEGMENTS.some((segment) => pathname.includes(segment));

  // beforeLoad only runs on navigation, so an already-open session would keep
  // rendering an app it can no longer write to. The 403 handler in lib/agent.ts
  // refetches the profile after a blocked write, which lands here.
  const lockRedirect = profile && resolveLockRedirect(profile);
  if (lockRedirect && !pathname.includes(lockRouteSegment(lockRedirect))) {
    return <Navigate to={lockRedirect} params={{ slug }} />;
  }

  if (isLockScreen) return <Outlet />;

  return (
    <AppLayout>
      <Outlet />
    </AppLayout>
  );
}
